const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

const asId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const asNullableString = (value) => {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : String(value);
};

const normalizeMoney = (value, fallbackCurrency = null) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value.amount === null || value.amount === undefined || value.amount === '') return null;
    return {
      amount: asNullableString(value.amount),
      currency: asNullableString(value.currency ?? fallbackCurrency),
    };
  }
  return { amount: asNullableString(value), currency: asNullableString(fallbackCurrency) };
};

const normalizeStrategy = (value, strategyId = null) => {
  if (!value && !strategyId) return null;
  if (typeof value === 'string') {
    return { id: asId(strategyId), name: value, slug: null };
  }
  const strategy = asObject(value);
  return {
    id: asId(strategy.id ?? strategyId),
    name: asNullableString(strategy.name),
    slug: asNullableString(strategy.slug),
  };
};

const normalizeAsset = (raw, fallback = {}) => {
  const asset = asObject(raw);
  const providers = asObject(asset.provider_symbols);
  const logo = asObject(asset.logo);
  return {
    id: asId(asset.id ?? asset.asset_id ?? fallback.asset_id),
    symbol: asNullableString(asset.symbol ?? fallback.symbol),
    name: asNullableString(asset.name),
    exchange: asNullableString(asset.exchange ?? fallback.exchange ?? fallback.listing_exchange),
    assetClass: asNullableString(asset.asset_class),
    currency: asNullableString(asset.currency ?? fallback.currency),
    logoUrl: asNullableString(asset.logo_url ?? logo.url),
    logoKey: asNullableString(asset.logo_key ?? logo.key),
    tradingviewSymbol: asNullableString(providers.tradingview ?? asset.tradingview_symbol),
    mappingStatus: asNullableString(asset.mapping_status),
    mappingProvenance: asNullableString(asset.mapping_provenance),
  };
};

export const normalizeAssetMetadata = (payload) => normalizeAsset(asObject(payload).asset ?? payload);

const normalizeDimension = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return { status: value };
  return asObject(value);
};

const availabilityFrom = (raw) => {
  const availability = raw.availability;
  const availabilityStatus = typeof availability === 'string' ? availability : asObject(availability).status;
  const reason = raw.reason ?? raw.memory_status ?? asObject(availability).reason ?? null;
  const explicitStatus = availabilityStatus ?? raw.status ?? null;

  if (explicitStatus === 'failed' || explicitStatus === 'error') return { state: 'error', reason };
  if (explicitStatus === 'incomplete' || reason === 'incomplete_memory') return { state: 'incomplete', reason: reason ?? 'incomplete_memory' };
  if (explicitStatus === 'unavailable' || reason === 'no_linked_trading_cycle' || reason === 'no_authoritative_handoff_snapshot') {
    return { state: 'unavailable', reason };
  }
  if (explicitStatus === 'stale') return { state: 'stale', reason };
  if (explicitStatus === 'available') return { state: 'available', reason };
  return { state: null, reason };
};

export const normalizeGuardianSummary = (value) => {
  if (value === null || value === undefined) {
    return { state: 'unavailable', reason: 'unavailable', freshness: null };
  }
  const raw = asObject(value);
  const availability = availabilityFrom(raw);
  const hasEvidence = Boolean(
    raw.evaluation_id || raw.snapshot_id || raw.readiness || raw.strategy_match || raw.risk_reward || raw.risk_setup || raw.bias
  );
  const freshnessValue = raw.current_context_freshness ?? raw.freshness;
  const freshness = typeof freshnessValue === 'string' ? freshnessValue : asObject(freshnessValue).status ?? null;
  return {
    state: availability.state ?? (hasEvidence ? 'available' : 'unavailable'),
    reason: availability.reason,
    freshness,
    evaluationId: asId(raw.evaluation_id),
    snapshotId: asId(raw.snapshot_id ?? raw.handoff_snapshot_id),
    evaluatedAt: raw.evaluated_at ?? raw.captured_at ?? null,
    snapshotReason: raw.snapshot_reason ?? null,
    readiness: normalizeDimension(raw.readiness),
    strategyMatch: normalizeDimension(raw.strategy_match),
    riskReward: normalizeDimension(raw.risk_reward),
    riskSetup: normalizeDimension(raw.risk_setup),
    bias: normalizeDimension(raw.bias),
    timeframeAlignment: normalizeDimension(raw.timeframe_alignment),
  };
};

const normalizeFill = (value, index) => {
  const fill = asObject(value);
  return {
    id: asId(fill.id ?? fill.execution_id ?? fill.exec_id ?? index),
    executionId: asId(fill.execution_id ?? fill.exec_id),
    orderId: asId(fill.order_id),
    timestamp: fill.timestamp ?? fill.executed_at ?? fill.ts_utc ?? null,
    price: fill.price ?? null,
    quantity: fill.quantity ?? fill.qty_signed ?? null,
    side: asNullableString(fill.side),
    positionEffect: asNullableString(fill.position_effect),
  };
};

export const normalizeJournalEntry = (value) => {
  const raw = asObject(value);
  const id = asId(raw.id ?? raw.journal_entry_id);
  if (!id) return null;
  const currency = raw.currency ?? asObject(raw.net_pnl).currency ?? asObject(raw.gross_pnl).currency ?? null;
  const asset = normalizeAsset(raw.asset, raw);
  const legacyClosedAt = raw.closed_at ?? raw.exit_datetime ?? null;
  const legacyOpenedAt = raw.opened_at ?? raw.entry_datetime ?? null;
  const status = raw.status ?? (legacyClosedAt ? 'closed' : raw.exit_datetime === null ? 'open' : null);
  return {
    id,
    journalEntryId: id,
    tradeId: asId(raw.trade_id),
    accountId: asId(raw.account_id),
    orderIds: asArray(raw.order_ids).map(asId).filter(Boolean),
    executionIds: asArray(raw.execution_ids).map(asId).filter(Boolean),
    lotClosureIds: asArray(raw.lot_closure_ids).map(asId).filter(Boolean),
    broker: asNullableString(raw.broker),
    provenance: raw.provenance ?? raw.import_source ?? null,
    status: asNullableString(status)?.toLowerCase() ?? null,
    openedAt: legacyOpenedAt,
    closedAt: legacyClosedAt,
    side: asNullableString(raw.side ?? raw.position_type)?.toLowerCase() ?? null,
    quantity: raw.quantity ?? raw.qty ?? null,
    duration: raw.duration ?? null,
    asset,
    symbol: asset.symbol,
    entryPrice: raw.entry_price ?? null,
    exitPrice: raw.exit_price ?? null,
    stopLossPrice: raw.stop_loss_price ?? null,
    takeProfitPrice: raw.take_profit_price ?? null,
    plannedRisk: normalizeMoney(raw.planned_risk_amount, currency),
    plannedRMultiple: raw.planned_r_multiple ?? raw.risk_reward_ratio ?? null,
    realizedRMultiple: raw.realized_r_multiple ?? null,
    grossPnl: normalizeMoney(raw.gross_pnl, currency),
    fees: normalizeMoney(raw.fees, currency),
    netPnl: normalizeMoney(raw.net_pnl ?? raw.pnl, currency),
    pnlPercent: raw.pnl_percent ?? raw.profit_loss_percentage ?? null,
    outcome: asNullableString(raw.outcome),
    strategy: normalizeStrategy(raw.strategy, raw.strategy_id),
    riskLevel: asNullableString(raw.risk_level),
    tradeIdeaNotes: asNullableString(raw.trade_idea_notes) ?? '',
    emotionalStateNotes: asNullableString(raw.emotional_state_notes) ?? '',
    newsNotes: asNullableString(raw.news_notes) ?? '',
    newsReferences: asArray(raw.news_references),
    tags: asArray(raw.tags),
    classification: raw.classification ?? raw.mistake_classification ?? null,
    version: raw.version ?? raw.resource_version ?? null,
    updatedAt: raw.updated_at ?? null,
    guardianSummary: normalizeGuardianSummary(raw.guardian_summary),
    cycleLink: raw.cycle_link ?? raw.primary_cycle_link ?? null,
    fills: asArray(raw.fills).map(normalizeFill),
  };
};

export const normalizeJournalList = (payload) => {
  const envelope = Array.isArray(payload)
    ? { count: payload.length, next: null, previous: null, results: payload }
    : asObject(payload);
  if (!Array.isArray(envelope.results)) {
    throw new TypeError('Journal list response must contain a results array.');
  }
  const entries = envelope.results.map(normalizeJournalEntry).filter(Boolean);
  return {
    count: Number.isFinite(Number(envelope.count)) ? Number(envelope.count) : entries.length,
    next: envelope.next ?? null,
    previous: envelope.previous ?? null,
    datasetVersion: envelope.dataset_version ?? null,
    entries,
  };
};

export const normalizeJournalDetail = (payload) => {
  const root = asObject(payload);
  const entry = normalizeJournalEntry(root.journal_entry ?? root);
  if (!entry) throw new TypeError('Journal detail response is missing journal_entry_id.');
  return entry;
};

export const normalizeGuardianAnalysis = (payload) => {
  const root = asObject(payload);
  const analysis = root.guardian_analysis === null ? {} : asObject(root.guardian_analysis ?? root);
  const combined = root.guardian_analysis === undefined ? root : { ...analysis, ...root, guardian_analysis: undefined };
  const availability = availabilityFrom(combined);
  const hasEvidence = Boolean(
    analysis.evaluation_id || analysis.snapshot_id || analysis.readiness || analysis.strategy_match || analysis.risk_reward || analysis.risk_setup
  );
  const freshnessValue = combined.current_context_freshness ?? combined.freshness;
  const freshness = typeof freshnessValue === 'string' ? freshnessValue : asObject(freshnessValue).status ?? null;
  return {
    state: availability.state ?? (hasEvidence ? 'available' : 'unavailable'),
    reason: availability.reason,
    freshness,
    journalEntryId: asId(combined.journal_entry_id ?? analysis.journal_entry_id),
    tradeId: asId(combined.trade_id ?? analysis.trade_id),
    tradingCycleId: asId(combined.trading_cycle_id ?? analysis.trading_cycle_id),
    cycleRoute: combined.cycle_route ?? asObject(combined.cycle_link).route ?? null,
    evaluationId: asId(combined.evaluation_id ?? analysis.evaluation_id),
    snapshotId: asId(combined.snapshot_id ?? combined.handoff_snapshot_id ?? analysis.snapshot_id),
    evaluatedAt: combined.evaluated_at ?? combined.captured_at ?? analysis.evaluated_at ?? null,
    snapshotReason: combined.snapshot_reason ?? analysis.snapshot_reason ?? null,
    evaluationMode: combined.evaluation_mode ?? analysis.evaluation_mode ?? null,
    planDigest: combined.plan_digest ?? analysis.plan_digest ?? null,
    planVersion: combined.plan_version ?? analysis.plan_version ?? null,
    contractVersion: combined.contract_version ?? analysis.contract_version ?? null,
    engineVersion: combined.engine_version ?? analysis.engine_version ?? null,
    rulesetVersion: combined.ruleset_version ?? analysis.ruleset_version ?? null,
    modelVersion: combined.model_version ?? analysis.model_version ?? null,
    factorVersion: combined.factor_version ?? analysis.factor_version ?? null,
    provenance: combined.provenance ?? analysis.provenance ?? null,
    readiness: normalizeDimension(analysis.readiness),
    strategyMatch: normalizeDimension(analysis.strategy_match),
    riskReward: normalizeDimension(analysis.risk_reward),
    riskSetup: normalizeDimension(analysis.risk_setup),
    bias: normalizeDimension(analysis.bias),
    timeframeAlignment: normalizeDimension(analysis.timeframe_alignment),
    blockers: asArray(analysis.blockers),
    warnings: asArray(analysis.warnings),
    passes: asArray(analysis.passes),
    missingEvidence: asArray(analysis.missing_evidence),
    acknowledgements: asArray(analysis.acknowledgements),
    permittedOverrides: asArray(analysis.permitted_overrides ?? analysis.overrides),
    nextSafeAction: analysis.next_safe_action ?? null,
  };
};

const normalizeLedgerRow = (value, index) => {
  const row = asObject(value);
  return {
    id: asId(row.id ?? row.ledger_id ?? row.event_id ?? index),
    sequence: row.sequence ?? index + 1,
    timestamp: row.timestamp ?? row.ts_utc ?? null,
    eventType: asNullableString(row.event_type ?? row.row_type ?? row.type),
    leg: asNullableString(row.leg),
    positionEffect: asNullableString(row.position_effect),
    venue: asNullableString(row.venue),
    orderId: asId(row.order_id),
    executionId: asId(row.execution_id ?? row.exec_id),
    quantity: row.quantity ?? row.qty_signed ?? null,
    price: row.price ?? null,
    gross: normalizeMoney(row.gross ?? row.gross_money, row.currency),
    fees: normalizeMoney(row.fees ?? row.commission, row.currency),
    costBasis: normalizeMoney(row.cost_basis, row.currency),
    realizedPnl: normalizeMoney(row.realized_pnl, row.currency),
    note: asNullableString(row.note),
    provenance: row.provenance ?? null,
  };
};

export const normalizeLedger = (payload) => {
  const root = asObject(payload);
  if (!Array.isArray(root.rows)) throw new TypeError('Ledger response must contain a rows array.');
  const totals = asObject(root.totals);
  const reconciliation = asObject(root.reconciliation);
  return {
    state: root.status ?? (reconciliation.status === 'incomplete' ? 'incomplete' : 'available'),
    reason: root.reason ?? null,
    journalEntryId: asId(root.journal_entry_id),
    rows: root.rows.map(normalizeLedgerRow),
    totals: {
      grossPnl: normalizeMoney(totals.gross_pnl ?? totals.gross, totals.currency),
      fees: normalizeMoney(totals.fees, totals.currency),
      netPnl: normalizeMoney(totals.net_pnl, totals.currency),
    },
    reconciliation: {
      status: reconciliation.status ?? null,
      difference: normalizeMoney(reconciliation.difference, reconciliation.currency),
      version: reconciliation.version ?? root.reconciliation_version ?? null,
    },
    provenance: root.provenance ?? null,
    version: root.version ?? null,
  };
};

export const normalizeStrategies = (payload) => {
  const values = Array.isArray(payload) ? payload : asArray(asObject(payload).results);
  return values
    .map((value) => {
      const item = asObject(value);
      const id = asId(item.id);
      if (!id || !item.name) return null;
      return { id, name: String(item.name), slug: asNullableString(item.slug) };
    })
    .filter(Boolean);
};

export const normalizeDemoReset = (payload) => {
  const raw = asObject(payload);
  return {
    accountId: asId(raw.account_id ?? 'demo'),
    datasetVersion: raw.dataset_version ?? null,
    affectedResources: asArray(raw.affected_resources),
  };
};

export const formatEvidenceText = (value) => {
  if (typeof value === 'string') return value;
  const item = asObject(value);
  return item.label ?? item.statement ?? item.explanation ?? item.reason ?? item.code ?? 'Evidence recorded';
};
